import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Store de tema (oscuro / claro) para el Dashboard KumIA.
 *
 * - Persiste en localStorage bajo la clave 'kumia-theme-storage'.
 * - Por defecto: 'dark' (el tema actual del sistema).
 * - Aplica la clase `.dark` en <html> (Tailwind darkMode: ["class"]) y sincroniza
 *   `color-scheme` para que los controles nativos (scrollbars, inputs date) sigan el tema.
 *
 * El parpadeo inicial (FOUC) se evita con un script inline en public/index.html
 * que aplica la clase ANTES de montar React, leyendo esta misma clave.
 */

const STORAGE_KEY = 'kumia-theme-storage';

/** Aplica el tema al documento. Seguro en SSR / entornos sin DOM. */
export const applyThemeToDocument = (theme) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const isDark = theme === 'dark';
  root.classList.toggle('dark', isDark);
  root.style.colorScheme = isDark ? 'dark' : 'light';
};

export const useThemeStore = create(
  persist(
    (set, get) => ({
      theme: 'dark',

      setTheme: (theme) => {
        applyThemeToDocument(theme);
        set({ theme });
      },

      toggleTheme: () => {
        const next = get().theme === 'dark' ? 'light' : 'dark';
        applyThemeToDocument(next);
        set({ theme: next });
      },

      isDark: () => get().theme === 'dark',
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ theme: state.theme }),
      // Al rehidratar desde localStorage, re-sincroniza el DOM con el valor guardado.
      onRehydrateStorage: () => (state) => {
        if (state?.theme) applyThemeToDocument(state.theme);
      },
    }
  )
);
